import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { DollarSign, TrendingUp, Package, Calendar, RefreshCw, Activity } from 'lucide-react';

export default function Revenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.revenueSummary(days);
      setData(res.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load revenue'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [days]);

  if (loading && !data) return <div style={s.loading}>Loading...</div>;
  if (!data) return <div style={{ ...s.loading, color: '#ff4466' }}>{error || 'No data'} <button onClick={load} style={{ marginLeft: 10 }}>Retry</button></div>;

  const maxRev = Math.max(...data.daily_breakdown.map(d => d.revenue), 1);
  const sizeLabels = { S: 'Small', M: 'Medium', L: 'Large' };

  const stats = [
    { label: 'Today', value: data.today, icon: Calendar, color: '#8BC63E' },
    { label: 'Yesterday', value: data.yesterday, icon: Calendar, color: '#4d90fe' },
    { label: 'This Week', value: data.this_week, icon: TrendingUp, color: '#ffb020' },
    { label: 'This Month', value: data.this_month, icon: DollarSign, color: '#ff6e7a' },
  ];

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Revenue</h1>
        <div style={s.headerRight}>
          <select style={s.select} value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button style={s.refreshBtn} onClick={load}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Top stats */}
      <div style={s.statGrid}>
        {stats.map((stat, i) => (
          <div key={i} style={{ ...s.statCard, borderColor: stat.color + '33' }}>
            <div style={{ ...s.statIcon, background: stat.color + '1a', color: stat.color }}>
              <stat.icon size={20} />
            </div>
            <div style={s.statLabel}>{stat.label}</div>
            <div style={s.statValue}>{stat.value.toFixed(2)} <span style={s.currency}>₾</span></div>
          </div>
        ))}
      </div>

      {/* Period summary */}
      <div style={s.summaryCard}>
        <div style={s.summaryGrid}>
          <div style={s.summaryItem}>
            <div style={s.summaryLabel}>Total Revenue ({days} days)</div>
            <div style={s.summaryValue}>{data.period_total.toFixed(2)} ₾</div>
          </div>
          <div style={s.summaryItem}>
            <div style={s.summaryLabel}>Transactions</div>
            <div style={s.summaryValue}>{data.period_transactions}</div>
          </div>
          <div style={s.summaryItem}>
            <div style={s.summaryLabel}>Avg Transaction</div>
            <div style={s.summaryValue}>{data.avg_transaction.toFixed(2)} ₾</div>
          </div>
        </div>
      </div>

      {/* Daily chart */}
      <div style={s.card}>
        <div style={s.cardTitle}><Activity size={16} /> Daily Revenue</div>
        <div style={s.chart}>
          {data.daily_breakdown.map((d, i) => {
            const h = Math.max((d.revenue / maxRev) * 180, d.revenue > 0 ? 4 : 2);
            const day = parseInt(String(d.date).slice(8, 10), 10);
            const showLabel = i % Math.ceil(data.daily_breakdown.length / 10) === 0;
            return (
              <div key={d.date} style={s.bar}>
                <div style={s.barValue}>{d.revenue > 0 ? d.revenue.toFixed(1) : ''}</div>
                <div style={{
                  ...s.barFill,
                  height: h + 'px',
                  background: d.revenue > 0 ? 'linear-gradient(180deg, #8BC63E, #00b4d8)' : '#252733'
                }} />
                <div style={s.barLabel}>{showLabel ? day : ''}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By box size */}
      {data.by_box_size && data.by_box_size.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}><Package size={16} /> Revenue by Box Size</div>
          <div style={s.sizeGrid}>
            {data.by_box_size.map(item => (
              <div key={item.size} style={s.sizeCard}>
                <div style={s.sizeLabel}>{sizeLabels[item.size] || item.size}</div>
                <div style={s.sizeValue}>{item.revenue.toFixed(2)} ₾</div>
                <div style={s.sizeCount}>{item.count} transactions</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  loading: { padding: 40, textAlign: 'center', color: '#8789a0' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#e4e5ed' },
  headerRight: { display: 'flex', gap: 10, alignItems: 'center' },
  select: { background: '#0f1117', border: '1px solid #252733', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#e4e5ed', cursor: 'pointer' },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#252733', color: '#e4e5ed', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 },
  statCard: { background: '#0f1117', border: '1px solid', borderRadius: 14, padding: 20 },
  statIcon: { width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  statLabel: { fontSize: 12, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  statValue: { fontSize: 24, fontWeight: 700, color: '#e4e5ed' },
  currency: { fontSize: 14, color: '#8789a0', fontWeight: 400 },
  summaryCard: { background: '#0f1117', border: '1px solid #252733', borderRadius: 14, padding: 20, marginBottom: 16 },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 },
  summaryItem: { borderRight: '1px solid #252733', paddingRight: 24 },
  summaryLabel: { fontSize: 12, color: '#8789a0', marginBottom: 6 },
  summaryValue: { fontSize: 22, fontWeight: 700, color: '#e4e5ed' },
  card: { background: '#0f1117', border: '1px solid #252733', borderRadius: 14, padding: 20, marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 600, color: '#e4e5ed', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 },
  chart: { display: 'flex', alignItems: 'flex-end', gap: 3, height: 220, padding: '20px 0 0' },
  bar: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%' },
  barValue: { fontSize: 9, color: '#8BC63E', marginBottom: 4, minHeight: 12 },
  barFill: { width: '100%', borderRadius: '4px 4px 0 0', transition: 'height 0.3s' },
  barLabel: { fontSize: 10, color: '#565870', marginTop: 6, minHeight: 14 },
  sizeGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
  sizeCard: { background: '#0a0b0f', border: '1px solid #252733', borderRadius: 10, padding: 16, textAlign: 'center' },
  sizeLabel: { fontSize: 12, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  sizeValue: { fontSize: 20, fontWeight: 700, color: '#8BC63E', marginBottom: 4 },
  sizeCount: { fontSize: 11, color: '#565870' },
};
